import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../models/member.school.model';
import { MembershipStatus } from '../../models/membership.status';
import {
  teacherAdapter,
  TeacherState,
} from '../reducers/teacher.school.reducer';
import { selectRouteParams } from './router.selector';

export const getTeacherState =
  createFeatureSelector<TeacherState>('gradeMembers');
export const getIds = (state: TeacherState) => state.ids;
export const memberStatsLoading = (state: TeacherState) =>
  state.memberStatsLoading;

export const {
  selectIds: getSchoolMemberIds,
  selectEntities: getSchoolMemberEntities,
  selectAll: getAllSchoolMembers,
  selectTotal: getTotalSchoolMembers,
} = teacherAdapter.getSelectors(getTeacherState);

export const loading = createSelector(
  getTeacherState,
  (teacherState: TeacherState) => teacherState.loading,
);

export const inProgress = createSelector(
  getTeacherState,
  (teacherState: TeacherState) => teacherState.inProgress,
);

export const getSchoolMemberById = (memberId: string | null) =>
  createSelector(getAllSchoolMembers, selectRouteParams, (allItems, { id }) => {
    if (memberId === null) return undefined;
    if (allItems) {
      return allItems.find((item) => {
        return item.memberId === memberId && item.groupId === id;
      });
    } else {
      return null;
    }
  });

export const getStudentsByParentId = (parentId: string | null) =>
  createSelector(getAllSchoolMembers, selectRouteParams, (allItems, { id }) => {
    if (parentId === null) return [];
    if (allItems) {
      return allItems.filter((item) => {
        return item.parentId === parentId && item.groupId === id;
      });
    } else {
      return [];
    }
  });

export const getSchoolMembersByGroupId = (groupId: string) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    // if (allItems) {
    return allItems
      .filter((item) => {
        return item.groupId === groupId;
      })
      .sort(sortDescendingByModifiedDate);
  });

export const getGroupMembersByType = (
  memberType: SchoolMemberType[],
  groupId: string | null,
) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    if (!allItems || !groupId) return [];
    return allItems
      .filter((item) => {
        return (
          item.groupId === groupId &&
          item.memberType?.some((type) => memberType?.includes(type))
        );
      })
      .sort(sortDescendingByModifiedDate);
  });

export const getStudentsByGrade = ({
  gradeId,
  groupId,
  academicYear,
  status,
}: {
  gradeId: string;
  groupId: string | null;
  academicYear: number | null;
  status?: MembershipStatus[] | null;
}) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    if (!allItems || !groupId || !academicYear) return [];

    return allItems
      .filter((item) => {
        return (
          item.groupId === groupId &&
          item.memberType?.includes(SchoolMemberType.student) &&
          item.gradeId === gradeId &&
          item.gradeYear === academicYear &&
          (status ? status.includes(item.status!) : true)
        );
      })
      .sort(sortDescendingByModifiedDate);
  });

export const getGroupMembersByStatus = (
  groupId: string,
  status: MembershipStatus[],
) =>
  createSelector(getAllSchoolMembers, (allItems) => {
    // if (allItems) {
    return allItems
      .filter((item) => {
        return item.groupId === groupId && status?.includes(item.status!);
      })
      .sort(sortDescendingByModifiedDate);
  });

// Docs written with serverTimestamp() have no `modified` until the server
// confirms; treat those as the newest.
function modifiedMillis(m: SchoolMember): number {
  return m.modified?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
}

function sortDescendingByModifiedDate(
  a: SchoolMember,
  b: SchoolMember,
): number {
  return modifiedMillis(b) - modifiedMillis(a);
}
